import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { packCountAtom } from '../../atoms/packAtoms';
import { toast } from 'react-toastify';
import PackOpenModal from '../../components/PackOpenModal';
import { fetchUserPacks, openPack } from '../../api/packInventory';
import { fetchWallet } from '../../api/wallet';
import { fetchUserTeams } from '../../api/userTeams';
import VariationRatesTooltip from '../../components/VariationRatesTooltip';
import { getCardImageSrc, handleCardImageError } from '../../hooks/useCardImage';

const UserPacksPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const setPackCount = useSetAtom(packCountAtom);
    const [packs, setPacks] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [openedPack, setOpenedPack] = useState(null);
    const [packOpenKey, setPackOpenKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasTeam, setHasTeam] = useState(true);
    const fetchPacks = useCallback(async () => {
        setError(null);
        try {
            const [validPacks, walletData, teams] = await Promise.all([
                fetchUserPacks(currentUser.userID),
                fetchWallet(),
                fetchUserTeams().catch(() => []),
            ]);
            setPacks(validPacks);
            setWallet(walletData.balance);
            setHasTeam((teams || []).length > 0);
            const total = validPacks.reduce((sum, p) => sum + p.amount, 0);
            setPackCount(total);
        } catch (error) {
            setError('Error fetching packs');
            toast.error('Error fetching packs: ' + error.message);
            console.error('Error fetching packs:', error);
        }
    }, [currentUser, setPackCount]);

    useEffect(() => {
        if (currentUser) {
            fetchPacks();
        }
    }, [currentUser, fetchPacks]);

    const handleOpenPack = useCallback(async ({ seriesID }) => {
        setLoading(true);
        try {
            const data = await openPack({ seriesID });
            const cards = data.cards;
            const packMeta = packs.find(p => p.seriesID === seriesID);
            setOpenedPack({
                cards,
                seriesID,
                seriesName: packMeta?.Series?.name ?? 'Pack',
                seriesKey: packMeta?.Series?.seriesKey ?? 'default',
            });
            setPackOpenKey(k => k + 1);

            // Update the pack count
            setPacks(prevPacks => {
                const updated = prevPacks
                    .map(pack => {
                        if (pack.seriesID === seriesID) {
                            return { ...pack, amount: pack.amount - 1 };
                        }
                        return pack;
                    })
                    .filter(pack => pack.amount > 0);
                const newTotal = updated.reduce((sum, p) => sum + p.amount, 0);
                setPackCount(newTotal);
                return updated;
            });
        } catch (error) {
            toast.error('Error opening pack: ' + error.message);
            console.error('Error opening pack:', error);
        } finally {
            setLoading(false);
        }
    }, [packs, setPackCount]);


    const sortedPacks = useMemo(() => [...packs].sort((a, b) => (b.Series?.year ?? 0) - (a.Series?.year ?? 0)), [packs]);

    if (!currentUser) {
        return <p>Please log in to view your packs.</p>;
    }

    return (
        <div className="user-packs-page">
            <div className="header-info">
                <h2>
                    My Packs /{' '}
                    <Link to="/packs/buy" className="button">Buy Packs</Link>
                </h2>
                <h2>${wallet}</h2>
            </div>
            {error && <p className="error">{error}</p>}

            <div className="packs-grid">
                {sortedPacks.map(pack => {
                    const id = pack.seriesID;
                    const key = pack.Series && pack.Series.seriesKey;
                    const name = pack.Series && pack.Series.name;
                    return (
                        <div key={id} className="pack-item">
                            <img
                                src={getCardImageSrc(key)}
                                alt={`${name} pack art`}
                                className="pack-image"
                                onError={handleCardImageError}
                            />
                            <div className="pack-details">
                                <span className="pack-title">
                                    {name} ({pack.amount})
                                    <VariationRatesTooltip seriesID={id} />
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleOpenPack({
                                            seriesID: pack.seriesID,
                                        })
                                    }
                                    disabled={loading}
                                >
                                    {loading ? 'Opening Pack...' : 'Open Pack'}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            {sortedPacks.length === 0 && !hasTeam && (
                <div className="packs-empty-nudge">
                    <p>You&rsquo;ve got cards &mdash; now build your team.</p>
                    <Link to="/team/create" className="button">Build Team &rarr;</Link>
                </div>
            )}
            {openedPack && (
                <PackOpenModal
                    key={packOpenKey}
                    cards={openedPack.cards}
                    seriesName={openedPack.seriesName}
                    seriesKey={openedPack.seriesKey}
                    onClose={() => setOpenedPack(null)}
                    remainingCount={packs.find(p => p.seriesID === openedPack.seriesID)?.amount ?? 0}
                    onOpenAnother={() => handleOpenPack({ seriesID: openedPack.seriesID })}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default UserPacksPage;
